import React, { createContext, ReactNode, useContext, useEffect, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { initProjectConfigData } from '../data/initialize/projectConfig.data';
import { fromDecimalNCR } from '../helpers/language';
import { ProjectConfigActionKind, projectConfigReducer } from '../reducers/ProjectConfigReducer';
import { IColor, IMarker, IProjectConfig, IStreets, IVehicleGroups } from '../interface/config/project-config.interface';
import { AuthActionKind, authReducer } from '../reducers/AuthReducer';
import useTimeSpentOnPage from '../hooks/useTimeSpentOnPage';
import useGenerateJwtToken from '../axios/useGenerateJwtToken.api';
import useGetSystemTableApi from '../axios/useGetSystemTable.api';
import { IAuth } from '../interface/auth.interface';
import { useSetUseNewApiApp } from '../stores/app.store';
import determineIfNewApi from '../helpers/determine-if-new-api';


interface ProjectConfigContextProps {
    projectConfigState: IProjectConfig;
    authState: IAuth;
    isProjectConfigLoading: boolean;
    minutesSpentInCompenent: number;
    setProjectId: (projectId: number) => void;
    setStreets: (streets: IStreets[]) => void;
    loadProjectConfig: () => Promise<void>;
    projectConfigMessage: string;
}

export const ProjectConfigContext = createContext<ProjectConfigContextProps | undefined>(undefined);

interface ProjectConfigProviderProps {
    children: ReactNode;
}

export const ProjectConfigProvider = ({ children }: ProjectConfigProviderProps) => {

    const navigate = useNavigate();

    const setUseNewApiApp = useSetUseNewApiApp();
    const { generateToken } = useGenerateJwtToken();
    const getProjectConfigDetailsApi = useGetSystemTableApi();

    const [projectConfigState, projectConfigDispatch] = useReducer(projectConfigReducer, initProjectConfigData);
    const [isProjectConfigLoading, setIsProjectConfigLoading] = useState(false);

    const [authState, authDispatch] = useReducer(authReducer, { token: '', loggin: false });
    const { minutesSpentInCompenent, resetTime } = useTimeSpentOnPage();
    const [projectConfigMessage, setProjectConfigMessage] = useState('');

    const spendTooMuchTimeOnPageText = 'חלף יותר מידי זמן אנא כנס לאתר דרך הקישור הראשי';
    const loginOrRetriveSystemTaleFailedText = 'התחברות נכשלה או נתוני המערכת לא נטענו לפרוייקט זה - אנא פנה לתמיכה';

    function setToken(token: string) {

        authDispatch({
            type: AuthActionKind.SET_TOKEN,
            token: token,
            loggin: true
        });
    }

    function setProjectId(projectId: number) {

        setUseNewApiApp(determineIfNewApi(projectId));

        projectConfigDispatch({
            type: ProjectConfigActionKind.SET_PROJECT_ID,
            payload: { projectId: projectId }
        });
    }

    async function loadProjectConfig() {

        if (projectConfigState.projectId === -1) return;

        const isNewApi = determineIfNewApi(projectConfigState.projectId);

        setIsProjectConfigLoading(true);

        const token = await refreshToken(isNewApi);

        if (!token) {

            logOut(loginOrRetriveSystemTaleFailedText);
            setIsProjectConfigLoading(false);
            return;
        }

        const isSuccess = await getProjectConfigDetails(token, isNewApi);

        if (!isSuccess) {

            logOut(loginOrRetriveSystemTaleFailedText);
            setIsProjectConfigLoading(false);
            return;
        }

        setIsProjectConfigLoading(false);
    }

    function logOut(message: string = '') {

        authDispatch({ type: AuthActionKind.LOG_OUT });

        setProjectId(-1);

        setProjectConfigMessage(message);
        resetTime();

        navigate('notFound');
    }

    async function getProjectConfigDetails(token: string, isNewApi: boolean): Promise<boolean> {

        const response = await getProjectConfigDetailsApi(token, isNewApi);

        if (!response.isSuccess) {

            return false;
        }

        const projectConfig = response.projectConfig;

        projectConfig.makers = projectConfig.makers.sort((a: IMarker, b: IMarker) => a.name.localeCompare(b.name));
        projectConfig.colors = projectConfig.colors.sort((a: IColor, b: IColor) => a.name.localeCompare(b.name));
        projectConfig.streets = projectConfig.streets.sort((a: IStreets, b: IStreets) => a.name.localeCompare(b.name));
        projectConfig.vehicleGroups = projectConfig.vehicleGroups.sort((a: IVehicleGroups, b: IVehicleGroups) => a.vehicleGroupName.localeCompare(b.vehicleGroupName));
        projectConfig.hebName = fromDecimalNCR(projectConfig.hebName);


        projectConfigDispatch({
            type: ProjectConfigActionKind.LOAD_PROJECT_CONFIG,
            payload: { projectConfig: projectConfig }
        });

        return true;
    }

    async function refreshToken(isNewApi: boolean): Promise<string> {

        const token = await generateToken(projectConfigState.projectId, isNewApi);

        if (!token) {

            logOut(loginOrRetriveSystemTaleFailedText);
            return '';
        }

        setToken(token);
        return token;
    }

    function setStreets(streets: IStreets[]) {

        projectConfigDispatch({
            type: ProjectConfigActionKind.SET_STREETS,
            payload: { streets: streets }
        });
    }

    useEffect(() => {

        loadProjectConfig();
    }, [projectConfigState.projectId]);

    useEffect(() => {

        if (!authState.loggin) {
            return;
        }

        if (minutesSpentInCompenent > 0 && (minutesSpentInCompenent % 20) === 0) {
            refreshToken(determineIfNewApi(projectConfigState.projectId));
        }

        if (minutesSpentInCompenent >= 90) {
            logOut(spendTooMuchTimeOnPageText);
        }

    }, [minutesSpentInCompenent]);

    return (
        <ProjectConfigContext.Provider value={{
            projectConfigState,
            setProjectId, setStreets,
            loadProjectConfig, authState,
            isProjectConfigLoading, minutesSpentInCompenent,
            projectConfigMessage
        }}>
            {children}
        </ProjectConfigContext.Provider>
    );
}

export const useProjectConfigContext = () => useContext(ProjectConfigContext);